import { useEffect, useState } from 'react';
import { UseFormGetValues, UseFormReset, UseFormWatch } from 'react-hook-form';
import {
  Container,
  Grid,
  Stack,
  Typography,
  IconButton,
  Box,
  Button,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridCloseIcon } from '@mui/x-data-grid';

import Form from 'components/Form';
import { TGender } from 'apis/account/type';
import adornment from 'components/Form/adornment';
import MuiTextField from 'components/Form/MuiTextField';

import useNotification from 'hooks/useNotification';
import { FormProps } from '../PatientList/api/type';
import SearchPatientId from './SearchPatientId';
import { IUpdatePatient } from './type';

interface Props extends FormProps {
  reset: UseFormReset<IUpdatePatient>;
  watch: UseFormWatch<IUpdatePatient>;
  getValues: UseFormGetValues<IUpdatePatient>;
  setIsSearched: (value: boolean) => void;
  isSearched: boolean;
}

const FC_OPTIONS = [
  'Full Code',
  'DNR',
  'DNI',
  'DNAR (no CPR)',
  'DNAR (Limited)',
  'DNAR (Comfort)',
  'AND',
  'Limited Code',
  'Partial Code',
];

const PatientInfo = (props: Props) => {
  const {
    register,
    reset,
    setValue,
    getValues,
    setIsSearched,
    watch,
    isSearched,
  } = props;

  const { onSuccess } = useNotification();

  const onChangePatientId = (e: any) => {
    const { value } = e.target;
    const prev = getValues('patient.patientId');
    if (value !== prev) setIsSearched(false);
    setValue('patient.patientId', value);
  };

  const handleReset = () => {
    reset({ patient: { gender: 1 } });
    setIsSearched(false);
  };

  // ------ Patient photo: local/server display & delete logic ------
  const [localName, setLocalName] = useState('');
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const hasServerPhoto = Boolean(watch('patient.photoUrl'));
  const hasServerPhotoFileName = Boolean(watch('patient.photoFileName'));

  const deleteFile = async () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(null);
    setLocalName('');
    setValue('files', undefined as any, { shouldDirty: true });
    setValue('patient.photoFileName', '', { shouldDirty: true });
    setValue('patient.photoUrl', '', { shouldDirty: true });
    onSuccess('The file has been deleted.');
  };
  // ------------------------------------------------------------

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Typography
          sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}
        >
          <span style={{ fontWeight: '400', color: '#6A3A87' }}>
            Patient Info (Editable)
          </span>
          <span style={{ fontSize: '12px', color: 'gray' }}>
            For additions and modifications to items other than those marked
            with F0, please inquire through the SmartNurse ENR chatbot.
          </span>
        </Typography>

        <Grid container gap={2}>
          {/* Column 1 */}
          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item
                required
                label={
                  'Patient ID (Only virtual patients belonging to the school can be edited via patient ID search.)'
                }
              >
                <MuiTextField
                  {...register('patient.patientId')}
                  onChange={onChangePatientId}
                  InputProps={{
                    readOnly: isSearched,
                    endAdornment: (
                      <>
                        <SearchPatientId
                          reset={reset}
                          getValues={getValues!}
                          setIsSearched={setIsSearched}
                        />
                        {isSearched && (
                          <IconButton
                            size="small"
                            onClick={handleReset}
                            title="Reset"
                            sx={{ ml: 0.5 }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    ),
                  }}
                />
              </Form.Item>
              <Form.Item required label={'Patient Name'}>
                <MuiTextField
                  required
                  size="small"
                  {...register('patient.name')}
                />
              </Form.Item>
              <Form.Item required label={'Age'}>
                <MuiTextField
                  required
                  size="small"
                  {...register('patient.age')}
                />
              </Form.Item>
              <Form.Item required label={'Gender'}>
                <Form.MuiRadioGroup
                  i18nKey="GENDER"
                  values={[1, 2]}
                  defaultValue={
                    getValues('patient.gender')
                      ? getValues('patient.gender')
                      : 1
                  }
                  onChange={(value: TGender) =>
                    setValue('patient.gender', value)
                  }
                />
              </Form.Item>
              <Form.Item required label={'Height'}>
                <MuiTextField
                  InputProps={{ ...adornment(undefined, '') }}
                  {...register('patient.height')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          {/* Column 2 */}
          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'Weight'}>
                <MuiTextField
                  InputProps={{ ...adornment(undefined, '') }}
                  {...register('patient.weight')}
                />
              </Form.Item>
              <Form.Item required label={'Blood Type'}>
                <MuiTextField {...register('patient.blood')} />
              </Form.Item>
              <Form.Item required label={'HOD'}>
                <MuiTextField type="number" {...register('patient.adminHod')} />
              </Form.Item>
              <Form.Item label={'POD'}>
                <MuiTextField
                  type="number"
                  required={false}
                  {...register('patient.adminPod')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          {/* Column 3 */}
          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'Ward'}>
                <MuiTextField {...register('patient.ward')} />
              </Form.Item>
              <Form.Item label={'Room'}>
                <MuiTextField required={false} {...register('patient.room')} />
              </Form.Item>
              <Form.Item label={'Department'}>
                <MuiTextField
                  required={false}
                  {...register('patient.department')}
                />
              </Form.Item>
              <Form.Item label={'Attending Physician'}>
                <MuiTextField
                  required={false}
                  {...register('patient.mainDoctor')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          {/* Column 4 */}
          <Grid item xs={2.875}>
            <Stack spacing={1}>
              {/* Diagnosis codes: read-only */}
              <Form.Item required label={'Main Diagnosis Code'}>
                <MuiTextField
                  {...register('patient.diseaseMain')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Sub-diagnosis Code 1'}>
                <MuiTextField
                  {...register('patient.diseaseSub1')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Sub-diagnosis Code 2'}>
                <MuiTextField
                  {...register('patient.diseaseSub2')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Sub-diagnosis Code 3'}>
                <MuiTextField
                  {...register('patient.diseaseSub3')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Sub-diagnosis Code 4'}>
                <MuiTextField
                  {...register('patient.diseaseSub4')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'Sub-diagnosis Code 5'}>
                <MuiTextField
                  {...register('patient.diseaseSub5')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>

              <Form.Item label={'Full Code (FC)'}>
                <Autocomplete
                  freeSolo
                  forcePopupIcon
                  options={FC_OPTIONS}
                  defaultValue={getValues('patient.fc') ?? undefined}
                  onChange={(_, value) => setValue('patient.fc', value ?? null)}
                  onInputChange={(_, value) =>
                    setValue('patient.fc', value ?? null)
                  }
                  isOptionEqualToValue={(o, v) => o === v}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select or enter FC."
                    />
                  )}
                />
              </Form.Item>
            </Stack>
          </Grid>
        </Grid>

        {/* Patient photo upload / preview / delete + School Code */}
        <Grid container sx={{ maxWidth: 1200 }}>
          <Grid item xs={12} md={5.5}>
            <Form.Item label={'File'}>
              {localPreview ? (
                <Box
                  component="img"
                  src={localPreview}
                  alt="Patient"
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 183,
                    objectFit: 'contain',
                    mb: 1.5,
                    borderRadius: 1,
                    border: '1px solid lightgray',
                  }}
                />
              ) : hasServerPhoto ? (
                <Box
                  component="img"
                  src={getValues('patient.photoUrl')}
                  alt="Patient"
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 183,
                    objectFit: 'contain',
                    mb: 1.5,
                    borderRadius: 1,
                    border: '1px solid lightgray',
                  }}
                />
              ) : null}

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  value={
                    localName ||
                    (hasServerPhotoFileName
                      ? watch('patient.photoFileName')
                      : '')
                  }
                  placeholder="Please select a file."
                  InputProps={{ readOnly: true }}
                  sx={{ width: '350px' }}
                />
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept="image/*,video/*"
                    onChange={e => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (!file) return;

                      setValue(
                        'files',
                        [{ saved: false, path: file.name, file }] as any,
                        { shouldDirty: true }
                      );

                      setValue('patient.photoFileName', file.name, {
                        shouldDirty: true,
                      });

                      const url = URL.createObjectURL(file);
                      if (localPreview) URL.revokeObjectURL(localPreview);
                      setLocalPreview(url);
                      setLocalName(file.name);
                    }}
                  />
                </Button>
                {(localName || hasServerPhotoFileName) && (
                  <IconButton
                    size="small"
                    onClick={async () => {
                      await deleteFile();
                    }}
                  >
                    <GridCloseIcon />
                  </IconButton>
                )}
              </Stack>
            </Form.Item>
          </Grid>

          <Grid item xs={12} md={6}>
            <Form.Item required label={'School Code'}>
              <MuiTextField
                {...register('patient.collegeName')}
                InputProps={{ readOnly: true }}
                sx={{ width: '350px' }}
              />
            </Form.Item>
          </Grid>
        </Grid>

        {/* Allergy section */}
        <Stack>
          <Typography variant="h5">Allergy</Typography>
          <Form.Item fullWidth label={'Drug'} sx={{ mt: 1.5 }}>
            <TextField
              multiline
              fullWidth
              minRows={4}
              size="small"
              placeholder="Please enter details."
              {...register('patient.allergyDrug')}
            />
          </Form.Item>
          <Form.Item fullWidth label={'Food'} sx={{ mt: 1.5 }}>
            <TextField
              multiline
              fullWidth
              minRows={4}
              size="small"
              placeholder="Please enter details."
              {...register('patient.allergyFood')}
            />
          </Form.Item>
          <Form.Item fullWidth label={'Environmental'} sx={{ mt: 1.5 }}>
            <TextField
              multiline
              fullWidth
              minRows={4}
              size="small"
              placeholder="Please enter details."
              {...register('patient.allergyEnvironmental')}
            />
          </Form.Item>
        </Stack>

        {/* Precautions */}
        <Stack>
          <Typography variant="h5">Precautions</Typography>
          <Form.Item fullWidth label={'Caution'} sx={{ mt: 1.5 }}>
            <TextField
              multiline
              fullWidth
              minRows={4}
              size="small"
              placeholder="Please enter details."
              {...register('patient.caution')}
            />
          </Form.Item>
        </Stack>

        {/* Medical Record */}
        <Form.Item required fullWidth label={'Medical Record (Editable)'}>
          <MuiTextField
            multiline
            minRows={20}
            {...register('patient.medicalNote')}
          />
        </Form.Item>
      </Stack>
    </Container>
  );
};

export default PatientInfo;
